import React, { useEffect } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  BarElement,
  BarController,
  ChartOptions
} from 'chart.js'
import { Line, Bar } from 'react-chartjs-2'
import Loader from 'src/components/common/loader-components/Loader'
import 'chartjs-adapter-moment'
import { AnalyticsChartSignature, ConversionUnitType } from 'src/lib/types/dashboardAnalytics'
import { useTheme } from '@emotion/react'
import moment from 'moment'
import { getScaleFormat } from 'src/lib/utils/analyticsHelpers'
import { Typography } from '@mui/material'
import { FlaskEmpty } from 'mdi-material-ui'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Tooltip,
  Legend
)
interface ChartComponentProps {
  chartType: AnalyticsChartSignature
  data: any
  isLoading?: boolean
  selectedConversionUnit: ConversionUnitType
  scaleUnit?: 'hour' | 'day' | 'month'
  maxTicksLimit?: number
  isComparison?: boolean
}

const ChartComponent: React.FC<ChartComponentProps> = ({
  chartType,
  data,
  isLoading,
  selectedConversionUnit,
  scaleUnit,
  maxTicksLimit,
  isComparison = false
}) => {
  const theme: any = useTheme()
  const trendChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        titleColor: '#4D4D4D',
        titleFont: { size: 14, weight: '500' },
        bodyFont: { size: 12, weight: '500' },
        titleSpacing: 2,
        bodyColor: '#949494',
        padding: 16,
        footerColor: '#4D4D4D',
        footerFont: { size: 14, weight: '500' },

        displayColors: false,
        borderColor: 'rgba(0,0,0, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: function (context: any) {
            const label = context.dataset.label?.split('#')?.[0] || ''
            return `${label}`
          },
          title: function (context: any) {
            const count = context[0].parsed.y
            if (isComparison) {
              return `${moment(context[0].label.split('#')[0]).format(getScaleFormat(scaleUnit, true))}`
            }
            return `${moment(context[0].label.split('#')[0]).format(getScaleFormat(scaleUnit, true))}    ${count}${
              selectedConversionUnit === 'percentage' ? '%' : ''
            }`
          },
          beforeFooter: function (context: any) {
            if (!isComparison) return ''
            const dataIndex = context[0].dataIndex
            const normalCount = data?.datasets?.[0].data?.[dataIndex]
            const normalLabel = data?.datasets?.[0].label.split('#')[1]
            return `${normalLabel}    ${normalCount}${selectedConversionUnit === 'percentage' ? '%' : ''}`
          },
          footer: function (context: any) {
            if (!isComparison) return ''
            const dataIndex = context[0].dataIndex
            const comparisonCount = data?.datasets?.[1].data?.[dataIndex]
            const comparisonLabel = data?.datasets?.[1].label.split('#')[1]
            return `${comparisonLabel}    ${comparisonCount}${selectedConversionUnit === 'percentage' ? '%' : ''}`
          }
        }
      }
    },
    scales: {
      xAxis1: {
        grid: {
          drawBorder: false,
          display: false,
          color: '#f5f5f5'
        },
        ticks: {
          callback: function (value: any, index: number, ticks: any) {
            const newLabel =
              scaleUnit === 'hour'
                ? `${this.getLabelForValue(value)?.split('#')[0]?.split(' ')[3]} ${
                    this.getLabelForValue(value)?.split('#')[0]?.split(' ')[4]
                  }`
                : `${this.getLabelForValue(value)?.split('#')[0]?.split(' ')[0]} ${
                    this.getLabelForValue(value)?.split('#')[0]?.split(' ')[1]
                  }`
            return newLabel
          },
          color: '#949494',
          font: {
            size: 12
          },
          maxTicksLimit: maxTicksLimit,
          maxRotation: 0
        },
        bounds: 'ticks' as const
        // scaleBeginAtZero : true
        // type: 'time' as "timeseries",
        // time: {
        //   unit: scaleUnit
        // },
        // id: "xAxis1"
      },
      xAxis2: {
        grid: {
          drawBorder: false,
          display: false,
          color: '#f5f5f5'
        },
        ticks: {
          display: false
        },
        bounds: 'ticks' as const
        // scaleBeginAtZero : true
        // type: 'time' as "timeseries",
        // time: {
        //   unit: scaleUnit
        // },
        // id: "xAxis1"
      },
      y: {
        beginAtZero: true,
        ...(selectedConversionUnit === 'percentage'
          ? {
              ticks: {
                min: 0,
                max: 100,
                stepSize: 10,
                color: '#6E6E6E',
                font: {
                  size: 14,
                  weight: 'bold'
                },
                callback: function (value: any, index: number) {
                  return value.toFixed(0) + '%'
                }
              }
            }
          : {
              ticks: {
                min: 0,
                stepSize: 10,
                color: '#6E6E6E',
                font: {
                  size: 14,
                  weight: 'bold'
                }
              }
            }),

        bounds: 'ticks' as const,
        grid: {
          drawBorder: false,
          display: true,
          color: '#f5f5f5'
        }
      }
    }
  }

  const catagoryChartOption = {
    responsive: true,
    maxBarThickness: 36,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        align: 'start' as const,
        display: true,

        labels: {
          padding: 10,
          boxHeight: 40,
          usePointStyle: true,
          font: {
            size: 12,
            weight: '500' as const
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        titleColor: theme.palette.text.header,
        titleFont: { size: 14, weight: '600' },
        bodyFont: { size: 13, weight: '400' },
        titleSpacing: 2,
        bodyColor: theme.palette.text.secondary,
        padding: 16,

        displayColors: false,
        borderColor: 'rgba(0,0,0, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: function (context: any) {
            const paymentMode = context?.dataset?.label
            return `Mode: ${paymentMode}`
          },
          title: function (context: any) {
            const splitValue = selectedConversionUnit === 'count' ? `${context?.[0].raw}` : `${context?.[0]?.raw}%`
            return `Payment split: ${splitValue}`
          }
        }
      }
    },
    scales: {
      x: {
        min: 0,
        grid: {
          drawBorder: true,
          display: false,
          color: '#f5f5f5'
        },
        ticks: {
          display: false
        },
        ...(selectedConversionUnit === 'percentage' ? { max: 100 } : {})
      },
      y: {
        beginAtZero: true,
        display: false
      }
    }
  }

  const renderChart = () => {
    if (!Boolean(data?.datasets)) {
      return (
        <Typography
          variant='subtitle2'
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            whiteSpace: 'nowrap',
            border: '1px solid #f0f0f0',
            borderRadius: 2
          }}
        >
          No data available.
        </Typography>
      )
    }
    switch (chartType) {
      case 'trend_chart':
        return <Line options={trendChartOptions as any} data={data} />
      case 'category_chart':
        return <Bar options={catagoryChartOption} data={data} />
      default:
        return <Line options={trendChartOptions as any} data={data} />
    }
  }

  return <>{!Boolean(isLoading) ? renderChart() : <Loader />}</>
}

export default ChartComponent
